import { useState } from 'react';
import { trpc } from '@/trpc/client';
import { ResetDialog, type ResetTarget } from './ResetDialog';

interface ResetPanelProps {
  userId: string;
}

/**
 * Admin-only entry point that opens {@link ResetDialog} and dispatches the
 * `trpc.lineUser.reset` mutation on confirmation. On success the
 * observation `lineUser.get` query is invalidated so the page reflects the
 * wiped state without a manual refresh.
 */
export function ResetPanel({ userId }: ResetPanelProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();
  const mutation = trpc.lineUser.reset.useMutation({
    onSuccess: () => {
      void utils.lineUser.get.invalidate({ userId });
    },
  });

  const handleConfirm = (targets: ResetTarget[]) => {
    setOpen(false);
    mutation.mutate({ userId, targets });
  };

  return (
    <section className="rounded border border-red-200 bg-red-50 p-4">
      <h2 className="text-sm font-semibold text-red-800">データのリセット</h2>
      <p className="mt-1 text-xs text-red-700">
        このユーザーの DynamoDB レコード / Salesforce 紐付け / DLQ メッセージを削除します。
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={mutation.isPending}
        className="mt-3 rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        リセットする…
      </button>
      {mutation.isPending ? (
        <p className="mt-2 text-xs text-red-700">リセット中…</p>
      ) : mutation.error ? (
        <p className="mt-2 text-xs text-red-700">エラー: {mutation.error.message}</p>
      ) : mutation.isSuccess ? (
        <p className="mt-2 text-xs text-green-700">リセット完了。</p>
      ) : null}
      <ResetDialog open={open} onClose={() => setOpen(false)} onConfirm={handleConfirm} />
    </section>
  );
}
